import {
	Button,
	Checkbox,
	ChipConfirmModal,
	ChipSelect,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	Input,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@ondit/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Mail, MoreHorizontal, Trash2, UserPlus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useAccount } from "~/common/account/account";
import { useAccess, whyDisabled } from "~/core/access/index";
import {
	ApiError,
	api,
	type BrandGrant,
	type Me,
	type Member,
	type Org,
	type PendingInvite,
	type People,
} from "~/core/api/index";
import * as fmt from "~/core/format";
import { useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { Async } from "~/core/ui/states";

/** The account roles this screen hands out. An owner is not one of them. */
const ROLES = ["admin", "member"] as const;
const BRAND_ROLES = ["editor", "viewer"] as const;

/**
 * Who is in the account, and what each of them may touch.
 *
 * The screen is built around the thing that makes this product's permissions
 * different from a checkbox: a role is granted **per brand**. An agency with
 * six brands has a person on two of them, and "can edit" as one account-wide
 * switch is either too much for that person or too little for everybody else.
 *
 * So an administrator is described by their account role and nothing else —
 * they hold every brand, including the one added next week — and everybody else
 * is described by a list. The list is what the invitation carries, and it is
 * what the row shows without being opened.
 *
 * Seats are counted here too, and counted including invitations nobody has
 * answered: a seat held for somebody who has been told they are in is not a
 * free seat, and an account that could mail thirty links on a plan of three
 * would discover the number only when the thirty-first person complained.
 */
export function MembersPanel({ me, org }: { me: Me; org: Org }) {
	const state = useQuery({
		queryKey: [...keys.org(org.id), "people"],
		queryFn: () => api.people(org.id),
		...read,
	});

	return <Async state={state}>{(data) => <Roster me={me} org={org} data={data} />}</Async>;
}

function Roster({ me, org, data }: { me: Me; org: Org; data: People }) {
	const queryClient = useQueryClient();
	const refresh = () => void queryClient.invalidateQueries({ queryKey: keys.org(org.id) });
	// A change on this screen is a request its control has already made; the
	// mutation is what reads the account again once it lands.
	const change = useMutation({ mutationFn: (work: Promise<unknown>) => work, onSuccess: refresh });
	const invite = useMutation({
		mutationFn: (body: Parameters<typeof api.invite>[1]) => api.invite(org.id, body),
		onSuccess: refresh,
	});
	const t = useT();
	const { locale } = useI18n();
	const openAccount = useAccount();
	// `may_manage` answers "is this person an administrator" and nothing else,
	// so on a lapsed plan every control on this screen looked live and the route
	// refused it. `admin` here is that answer folded with the plan's, which is
	// the pair the write routes ask for. Removing a member and withdrawing an
	// invitation are the exceptions on the server too: shedding a seat is a way
	// back onto a smaller plan, not a change the plan has to be live for.
	const { admin, why } = useAccess();
	const mayChange = data.may_manage && admin;
	const [tab, setTab] = useState<"active" | "pending">("active");
	const [inviting, setInviting] = useState(false);
	const [removing, setRemoving] = useState<Member | null>(null);
	const [transferring, setTransferring] = useState<Member | null>(null);
	const [revoking, setRevoking] = useState<PendingInvite | null>(null);
	// The invitation a request is out for. Sending again is a mail, and two
	// clicks on it were two mails.
	const [acting, setActing] = useState<string | null>(null);

	const taken = data.seats.members + data.seats.pending;
	// Zero is the enterprise row: a number the contract sets, and there is
	// nothing here to count against.
	const full = data.seats.max > 0 && taken >= data.seats.max;

	async function run(work: Promise<unknown>, done?: string) {
		try {
			await change.mutateAsync(work);
			if (done) toast.success(done);
		} catch (error) {
			toast.error(error instanceof ApiError ? error.localized(locale) : String(error));
		}
	}

	async function actOn(invite: PendingInvite, work: Promise<unknown>, done?: string) {
		if (acting !== null) return;
		setActing(invite.id);
		try {
			await run(work, done);
		} finally {
			setActing(null);
		}
	}

	return (
		<>
			{/* The seat count first, because it is the answer to the question
			    somebody opening this screen is about to ask. */}
			<div className="seat-bar">
				<div className="seat-text">
					<strong>
						{data.seats.max > 0
							? t("members.seatsUsed", { used: taken, max: data.seats.max })
							: t("members.seatsUnlimited", { used: taken })}
					</strong>
					{data.seats.pending > 0 ? (
						<span className="hint">{t("members.seatsPending", { n: data.seats.pending })}</span>
					) : null}
				</div>
				{data.may_manage ? (
					full ? (
						// Full is not a dead end, and the row says where it leads
						// before it is clicked: a button labelled "Invite" that
						// opens the plan reads as a broken button.
						<Button onClick={() => openAccount("subscription")}>{t("members.seatsFull")}</Button>
					) : (
						<Button
							onClick={() => setInviting((open) => !open)}
							disabled={!mayChange}
							title={whyDisabled(mayChange, why.admin)}
						>
							<UserPlus />
							{t("members.invite")}
						</Button>
					)
				) : null}
			</div>

			{data.may_manage && inviting && !full ? (
				<InviteForm
					brands={data.brands}
					onCancel={() => setInviting(false)}
					onSend={async (body) => {
						try {
							const result = await invite.mutateAsync(body);
							// The invitation is the row, not the mail. When the mail
							// could not be sent the seat is still held and the link
							// can be sent again, and saying so is the difference
							// between waiting for a mail and knowing to resend.
							toast[result.sent ? "success" : "warning"](
								t(result.sent ? "members.invited" : "members.inviteUnsent", { email: result.email }),
							);
							setInviting(false);
						} catch (error) {
							toast.error(error instanceof ApiError ? error.localized(locale) : String(error));
						}
					}}
				/>
			) : null}

			<Tabs value={tab} onValueChange={(value) => setTab(value as "active" | "pending")} className="gap-(--s-6)">
				{data.invites.length > 0 ? (
					<TabsList aria-label={t("members.title")}>
						<TabsTrigger value="active">{t("members.active", { n: data.members.length })}</TabsTrigger>
						<TabsTrigger value="pending">{t("members.pending", { n: data.invites.length })}</TabsTrigger>
					</TabsList>
				) : null}

				<TabsContent value={tab}>
					{tab === "active"
						? data.members.map((member) => (
								<MemberRow
									key={member.user_id}
									member={member}
									brands={data.brands}
									you={member.user_id === me.user.id}
									// An administrator may not act on an owner: that is the
									// account's owner, and only another owner outranks them.
									manageable={data.may_manage && (data.role === "owner" || member.role !== "owner")}
									// Role and brand grants are a change the plan has to be
									// live for; removing the person is not.
									changeable={mayChange && (data.role === "owner" || member.role !== "owner")}
									why={why.admin}
									onRole={(role) => run(api.updateMember(org.id, member.user_id, { role }))}
									onBrands={(brands) => run(api.updateMember(org.id, member.user_id, { brands }))}
									// Only an owner hands the account over, and only to
									// somebody who is not already one.
									transferable={mayChange && data.role === "owner" && member.role !== "owner"}
									onTransfer={() => setTransferring(member)}
									onRemove={() => setRemoving(member)}
								/>
							))
						: data.invites.map((invite) => (
								<div className="member-row" key={invite.id}>
									<span className="account-mark" aria-hidden>
										<Mail />
									</span>
									<div className="member-text">
										<strong>{invite.email}</strong>
										<span className="hint">
											{t(`members.role.${invite.role}`)} ·{" "}
											{t("members.expires", { date: fmt.date(locale, invite.expires_at) })}
										</span>
										<BrandList brands={invite.brands} names={data.brands} all={invite.role === "admin"} />
									</div>
									<div className="member-controls">
										<Button
											variant="default"
											size="sm"
											disabled={acting !== null || !mayChange}
											title={whyDisabled(mayChange, why.admin)}
											onClick={() => actOn(invite, api.resendInvite(org.id, invite.id), t("members.resent"))}
										>
											{acting === invite.id ? t("login.working") : t("members.resend")}
										</Button>
										<Button variant="default" size="sm" disabled={acting !== null} onClick={() => setRevoking(invite)}>
											{t("members.revoke")}
										</Button>
									</div>
								</div>
							))}
				</TabsContent>
			</Tabs>
			<ChipConfirmModal
				open={removing !== null}
				onOpenChange={(open) => !open && setRemoving(null)}
				title={t("members.removeTitle")}
				text={t("members.removeBody", { who: removing?.name || removing?.email || "" })}
				dismissLabel={t("common.cancel")}
				confirm={{
					label: t("members.remove"),
					variant: "destructive",
					onClick: () => {
						if (removing) run(api.removeMember(org.id, removing.user_id));
						setRemoving(null);
					},
				}}
			/>
			{/* Handing the account over asks first: it is the one change on
			    this screen the person making it cannot undo alone. */}
			<ChipConfirmModal
				open={transferring !== null}
				onOpenChange={(open) => !open && setTransferring(null)}
				defaultAction="none"
				title={t("members.transferTitle")}
				text={t("members.transferBody", { who: transferring?.name || transferring?.email || "" })}
				dismissLabel={t("common.cancel")}
				confirm={{
					label: t("members.transfer"),
					// Not destructive: nothing is deleted, and the account has an
					// owner on both sides of the change.
					variant: "primary",
					onClick: () => {
						if (transferring)
							run(
								api.updateMember(org.id, transferring.user_id, { role: "owner" }),
								t("members.transferred", { who: transferring.name || transferring.email }),
							);
						setTransferring(null);
					},
				}}
			/>
			{/* Withdrawing asks first: the link in somebody's inbox stops working,
			    and the row is gone from here the moment it does. */}
			<ChipConfirmModal
				open={revoking !== null}
				onOpenChange={(open) => !open && setRevoking(null)}
				title={t("members.revokeTitle")}
				text={t("members.revokeBody", { email: revoking?.email ?? "" })}
				dismissLabel={t("common.cancel")}
				confirm={{
					label: t("members.revoke"),
					variant: "destructive",
					onClick: () => {
						if (revoking) void actOn(revoking, api.revokeInvite(org.id, revoking.id));
						setRevoking(null);
					},
				}}
			/>
		</>
	);
}

/**
 * One person, their account role, and the brands they hold.
 *
 * The brands are on the row rather than behind it: "what can this person see"
 * is the question the screen exists to answer, and an answer that takes a click
 * is an answer nobody checks.
 */
function MemberRow({
	member,
	brands,
	you,
	manageable,
	changeable,
	transferable,
	why,
	onRole,
	onBrands,
	onTransfer,
	onRemove,
}: {
	member: Member;
	brands: { id: string; name: string }[];
	you: boolean;
	/** May act on this person at all: an administrator, and not an owner. */
	manageable: boolean;
	/** …and the plan is live, which role and brand grants need and removal does not. */
	changeable: boolean;
	/** The viewer is the owner and this person is not: the account can go to them. */
	transferable: boolean;
	why: string;
	onRole: (role: string) => void;
	onBrands: (grants: BrandGrant[]) => void;
	onTransfer: () => void;
	onRemove: () => void;
}) {
	const t = useT();
	const { locale } = useI18n();
	const [editing, setEditing] = useState(false);
	const who = member.name || member.email;

	return (
		<div className="member-row">
			<span className="account-mark" aria-hidden>
				{who.slice(0, 1)}
			</span>
			<div className="member-text">
				<strong>
					{who}
					{you ? <span className="pill">{t("members.you")}</span> : null}
				</strong>
				<span className="hint">
					{member.email} ·{" "}
					{member.last_seen_at
						? t("members.lastSeen", { when: fmt.relative(locale, member.last_seen_at) ?? "—" })
						: t("members.joined", { date: fmt.date(locale, member.joined_at) })}
				</span>
				<BrandList brands={member.brands} names={brands} all={member.all_brands} />
				{editing ? (
					<BrandEditor
						brands={brands}
						value={member.brands}
						onCancel={() => setEditing(false)}
						onSave={(grants) => {
							onBrands(grants);
							setEditing(false);
						}}
					/>
				) : null}
			</div>
			<div className="member-controls">
				{/* An owner's role is not a select: there is one, it is not
				    handed out from here, and a disabled dropdown invites the
				    click anyway. Nobody changes their own role either — that is
				    how the last administrator locks an account out of itself. */}
				{manageable && member.role !== "owner" && !you ? (
					<ChipSelect
						value={member.role}
						onChange={onRole}
						disabled={!changeable}
						aria-label={t("members.roleLabel")}
						title={whyDisabled(changeable, why)}
						options={ROLES.map((role) => ({ value: role, label: t(`members.role.${role}`) }))}
					/>
				) : (
					<span className="pill">{t(`members.role.${member.role}`)}</span>
				)}
				{manageable && !member.all_brands ? (
					<Button
						variant="default"
						size="sm"
						disabled={!changeable}
						title={whyDisabled(changeable, why)}
						onClick={() => setEditing((open) => !open)}
					>
						{t("members.access")}
					</Button>
				) : null}
				{(transferable && !you) || (manageable && !you) ? (
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="ghost" size="sm" aria-label={t("common.more")}>
								<MoreHorizontal />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							{transferable && !you ? (
								<DropdownMenuItem onSelect={onTransfer}>{t("members.transfer")}</DropdownMenuItem>
							) : null}
							{manageable && !you ? (
								<DropdownMenuItem className="text-danger" onSelect={onRemove}>
									<Trash2 />
									{t("members.remove")}
								</DropdownMenuItem>
							) : null}
						</DropdownMenuContent>
					</DropdownMenu>
				) : null}
			</div>
		</div>
	);
}

/** What this person may open, in one line. */
function BrandList({
	brands,
	names,
	all,
}: {
	brands: BrandGrant[];
	names: { id: string; name: string }[];
	all: boolean;
}) {
	const t = useT();
	if (all) return <span className="hint">{t("members.everyBrand")}</span>;
	if (brands.length === 0) return <span className="hint">{t("members.noBrands")}</span>;
	return (
		<span className="member-brands">
			{brands.map((grant) => (
				<span className="pill" key={grant.entity_id}>
					{names.find((brand) => brand.id === grant.entity_id)?.name ?? grant.entity_id} ·{" "}
					{t(`members.brandRole.${grant.role}`)}
				</span>
			))}
		</span>
	);
}

/**
 * Which brands, and at what role on each.
 *
 * The whole set, every time: the list shows what somebody holds and sends back
 * what it shows, so a brand that is un-ticked is a brand taken away. A merge
 * would make "remove access" impossible to express.
 */
function BrandPicker({
	brands,
	value,
	onChange,
}: {
	brands: { id: string; name: string }[];
	value: BrandGrant[];
	onChange: (grants: BrandGrant[]) => void;
}) {
	const t = useT();
	const roleOf = (id: string) => value.find((grant) => grant.entity_id === id)?.role;

	const toggle = (id: string, on: boolean) =>
		onChange(
			on ? [...value, { entity_id: id, role: "viewer" as const }] : value.filter((grant) => grant.entity_id !== id),
		);
	const setRole = (id: string, role: "editor" | "viewer") =>
		onChange(value.map((grant) => (grant.entity_id === id ? { ...grant, role } : grant)));

	return (
		<div className="brand-picker">
			{brands.map((brand) => {
				const role = roleOf(brand.id);
				return (
					<div className="brand-pick" key={brand.id}>
						<Checkbox
							id={`grant-${brand.id}`}
							checked={role !== undefined}
							onCheckedChange={(next) => toggle(brand.id, next === true)}
						/>
						<label htmlFor={`grant-${brand.id}`}>{brand.name}</label>
						<ChipSelect
							value={role ?? "viewer"}
							onChange={(next) => setRole(brand.id, next as "editor" | "viewer")}
							disabled={role === undefined}
							aria-label={t("members.brandRoleLabel")}
							options={BRAND_ROLES.map((option) => ({ value: option, label: t(`members.brandRole.${option}`) }))}
						/>
					</div>
				);
			})}
		</div>
	);
}

/**
 * The brands one person holds, opened for editing.
 *
 * The draft is local until it is saved: an un-ticked brand is a brand being
 * taken away, and taking it away on the click rather than on the save means a
 * mis-click is a permission change with no way back.
 */
function BrandEditor({
	brands,
	value,
	onSave,
	onCancel,
}: {
	brands: { id: string; name: string }[];
	value: BrandGrant[];
	onSave: (grants: BrandGrant[]) => void;
	onCancel: () => void;
}) {
	const t = useT();
	const [draft, setDraft] = useState<BrandGrant[]>(value);

	return (
		<>
			<BrandPicker brands={brands} value={draft} onChange={setDraft} />
			<div className="row-actions">
				<Button size="sm" onClick={() => onSave(draft)}>
					{t("members.saveAccess")}
				</Button>
				<Button variant="default" size="sm" onClick={onCancel}>
					{t("common.cancel")}
				</Button>
			</div>
		</>
	);
}

/**
 * The invitation: an address, a role, and — for anybody who is not an
 * administrator — the brands they are being invited to.
 *
 * A member with no brand is somebody who signs in to an empty workspace and
 * files a bug, so the button stays off until at least one is ticked. The server
 * refuses the same request; this is the sentence that saves the round trip.
 */
function InviteForm({
	brands,
	onSend,
	onCancel,
}: {
	brands: { id: string; name: string }[];
	onSend: (body: { email: string; role: string; brands: BrandGrant[] }) => Promise<void>;
	onCancel: () => void;
}) {
	const t = useT();
	const [email, setEmail] = useState("");
	const [role, setRole] = useState<string>("member");
	const [grants, setGrants] = useState<BrandGrant[]>([]);
	const [busy, setBusy] = useState(false);

	const needsBrands = role !== "admin";

	async function submit(event: React.FormEvent) {
		event.preventDefault();
		setBusy(true);
		try {
			await onSend({ email, role, brands: needsBrands ? grants : [] });
		} finally {
			setBusy(false);
		}
	}

	return (
		<form className="panel invite-form" onSubmit={submit}>
			<div className="row-actions">
				<Input
					aria-label={t("login.email")}
					className="w-full min-w-0 sm:w-auto sm:flex-1"
					type="email"
					placeholder="name@acme.com"
					value={email}
					onChange={(event) => setEmail(event.target.value)}
					required
				/>
				<ChipSelect
					value={role}
					onChange={setRole}
					aria-label={t("members.roleLabel")}
					options={ROLES.map((value) => ({ value: value, label: t(`members.role.${value}`) }))}
				/>
				<Button type="submit" disabled={busy || !email || (needsBrands && grants.length === 0)}>
					{t("members.send")}
				</Button>
				<Button type="button" variant="default" onClick={onCancel}>
					{t("common.cancel")}
				</Button>
			</div>

			{needsBrands ? (
				<>
					<p className="faint">{t("members.accessLede")}</p>
					<BrandPicker brands={brands} value={grants} onChange={setGrants} />
				</>
			) : (
				<p className="faint">{t("members.adminLede")}</p>
			)}
		</form>
	);
}
